import Compressor from 'compressorjs';

/** Client: Bandbreite sparen vor dem Upload (Server normalisiert danach leicht nach). */
export const CLIENT_IMAGE_COMPRESS = {
    maxEdge: 1920,
    quality: 0.6,
    minBytes: 1024 * 1024, // 1 MB
};

function loadImageDimensions(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Bild konnte nicht geladen werden'));
        };
        img.src = url;
    });
}

function runCompressor(file) {
    return new Promise((resolve, reject) => {
        // eslint-disable-next-line no-new
        new Compressor(file, {
            quality: CLIENT_IMAGE_COMPRESS.quality,
            maxWidth: CLIENT_IMAGE_COMPRESS.maxEdge,
            maxHeight: CLIENT_IMAGE_COMPRESS.maxEdge,
            mimeType: 'image/jpeg',
            checkOrientation: true,
            success(result) {
                resolve(result);
            },
            error(err) {
                reject(err);
            },
        });
    });
}

/**
 * Komprimiert ein Bild für den Upload, behält den Original-Dateinamen.
 * Greift bei Größe > 1 MB oder langer Seite > 1920 px.
 * Nutzt das Ergebnis nur, wenn es kleiner als das Original ist.
 *
 * @param {File|Blob} file
 * @returns {Promise<File|Blob>}
 */
export async function compressImageForUpload(file) {
    if (!file || !(file.type || '').startsWith('image/')) {
        return file;
    }

    let needsCompress = file.size > CLIENT_IMAGE_COMPRESS.minBytes;
    if (!needsCompress) {
        try {
            const { width, height } = await loadImageDimensions(file);
            needsCompress =
                Math.max(width, height) > CLIENT_IMAGE_COMPRESS.maxEdge;
        } catch {
            return file;
        }
    }

    if (!needsCompress) {
        return file;
    }

    try {
        const result = await runCompressor(file);
        if (result.size >= file.size) {
            return file;
        }
        return new File([result], file.name, {
            type: result.type || 'image/jpeg',
            lastModified: Date.now(),
        });
    } catch (err) {
        console.warn(
            'Bildkompression fehlgeschlagen, Original wird verwendet:',
            err,
        );
        return file;
    }
}

/**
 * Run async mapper with limited parallelism (keeps mobile CPUs usable).
 * @template T, R
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<R>} mapper
 * @returns {Promise<R[]>}
 */
export async function mapWithConcurrency(items, concurrency, mapper) {
    const list = Array.from(items || []);
    const limit = Math.max(1, Math.min(concurrency || 1, list.length || 1));
    const results = new Array(list.length);
    let nextIndex = 0;

    async function worker() {
        while (nextIndex < list.length) {
            const index = nextIndex;
            nextIndex += 1;
            results[index] = await mapper(list[index], index);
        }
    }

    await Promise.all(Array.from({ length: limit }, () => worker()));
    return results;
}

/** Parallel client compress (default 3) — deutlich schneller als strikt sequentiell. */
export const CLIENT_COMPRESS_CONCURRENCY = 3;

/**
 * @param {FileList|File[]|Blob[]} files
 * @param {{ concurrency?: number }} [options]
 * @returns {Promise<Array<File|Blob>>}
 */
export async function compressImagesForUpload(files, options = {}) {
    const concurrency = options.concurrency || CLIENT_COMPRESS_CONCURRENCY;
    return mapWithConcurrency(files, concurrency, (file) =>
        compressImageForUpload(file),
    );
}

export function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('FileReader'));
        reader.readAsDataURL(file);
    });
}
